import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@/index.css";
import App from "@/App";
import AppErrorFallback from "@/components/AppErrorFallback";
import { installChunkReloadHandler } from "@/utils/chunkReload";
import { installVersionWatcher } from "@/utils/versionWatch";

// Stays silent when REACT_APP_SENTRY_DSN is unset (dev/local) — no console noise.
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
    release: process.env.REACT_APP_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
  });

  // Session Replay is the single heaviest Sentry module (~90KB gzip). Loading
  // it lazily after the page is idle keeps it out of the critical main bundle
  // — replay still attaches for error capture, just not during first paint.
  const loadReplay = () =>
    Sentry.lazyLoadIntegration('replayIntegration')
      .then((replayIntegration) =>
        Sentry.addIntegration(replayIntegration({ maskAllText: false, blockAllMedia: false })))
      .catch(() => {});
  if ('requestIdleCallback' in window) {
    requestIdleCallback(loadReplay, { timeout: 5000 });
  } else {
    setTimeout(loadReplay, 3000);
  }
}

// Catch stale-chunk errors after a deploy and auto-reload once.
// Prevents users on pre-deploy main.js from hitting blank screens when
// React tries to lazy-load a chunk that no longer exists at the old hash.
installChunkReloadHandler();

// Detect new deploys from long-lived tabs (checks /version.json on focus)
// and reload once so users always resume on the current build.
installVersionWatcher();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={AppErrorFallback} showDialog={false}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
